import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ExternalLink } from "lucide-react";
import { useSiteContent } from "../contexts/SiteContentContext";

function Stars({ count = 5 }) {
  return (
    <div className="flex items-center gap-0.5" aria-hidden>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className="w-3.5 h-3.5" fill={i < count ? "currentColor" : "none"} strokeWidth={1.5} />
      ))}
    </div>
  );
}

/**
 * Google recenze — bez API klíče: admin ručně edituje skóre/počet/recenze
 * v administraci (Texty webu). Jednotlivé recenze se automaticky přepínají
 * po 4 vteřinách. Odkaz vede na skutečnou stránku Google recenzí.
 */
export default function GoogleReviews() {
  const { text, links } = useSiteContent();
  const gr = text.google_reviews || {};
  const reviews = Array.isArray(gr.reviews) && gr.reviews.length > 0 ? gr.reviews : [];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (reviews.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 4000);
    return () => clearInterval(t);
  }, [reviews.length]);

  if (reviews.length === 0) return null;
  const current = reviews[idx] || reviews[0];

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, margin: "-60px" }}
      transition={{ duration: 0.6 }}
      className="mt-10 md:mt-12 rounded-2xl grid sm:grid-cols-[auto_1fr] gap-6 sm:gap-8 p-6 md:p-8 card-dark"
      data-testid="google-reviews-frame"
    >
      <div className="flex flex-col items-center sm:items-start gap-2 flex-shrink-0 text-center sm:text-left">
        <div className="flex items-end gap-1.5">
          <span className="font-bold text-krem-100 leading-none" style={{ fontSize: "52.8px" }} data-testid="google-reviews-rating">
            {gr.rating || "4.9"}
          </span>
          <span className="text-tlum-dark mb-1" style={{ fontSize: "15.4px" }}>/ 5</span>
        </div>
        <div className="text-akcent">
          <Stars count={Math.round(parseFloat(gr.rating) || 5)} />
        </div>
        <div className="text-tlum-dark" style={{ fontSize: "13.2px" }} data-testid="google-reviews-count">
          {gr.review_count || "0"} recenzí na Google
        </div>
        {links.googleReviews && (
          <a
            href={links.googleReviews}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 sm:mt-2 inline-flex items-center gap-1.5 text-akcent hover:text-krem-100 transition-colors"
            style={{ fontSize: "15.4px" }}
            data-testid="google-reviews-link"
          >
            Zobrazit na Google <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>

      <div className="relative min-h-[86px] pt-5 sm:pt-0 border-t sm:border-t-0 sm:border-l" style={{ borderColor: "var(--linka-tmava)" }}>
        <div className="sm:pl-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4 }}
              data-testid="google-reviews-quote"
            >
              <div className="text-akcent mb-2"><Stars count={current.stars || 5} /></div>
              <p className="text-krem-100/90 max-w-lg" style={{ fontSize: "15.71px", lineHeight: 1.5 }}>&bdquo;{current.text}&ldquo;</p>
              {current.author && <div className="mt-2 text-tlum-dark" style={{ fontSize: "12.65px", fontWeight: 500, letterSpacing: "0.08em" }}>{current.author}</div>}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
